import * as React from "react";
import { useSearchParams } from "react-router-dom";
import { useFetchTasks } from "../../../../hooks/queries/tasks";
import {
    TaskCategory,
    TaskListSortAttribute,
    TaskStatus,
} from "../../../../utils/types/tasks";
import { AppSortOrder } from "../../../../utils/types";
import { useFetchUserDetails } from "../../../../hooks/queries/user";
import ListState from "../../../../components/ListState/ListState";
import Loader from "../../../../shared-ui/Loader/Loader";
import Task from "../../../tasks/components/Task/Task";
import { CLIENT_CHART_TASK_CATEGORIES } from "../../../../utils/constants";

interface PendingTaskProps {
    /** Client ID for filtering */
    clientId?: string;
    /** Context in which component is used */
    context?:
        | "dashboard_my_tasks"
        | "dashboard_supervisee_tasks"
        | "client_chart_pending_tasks"
        | "client_chart_all_tasks";
}

export default function PendingTasks({
    clientId,
    context = "dashboard_my_tasks",
}: PendingTaskProps) {
    const loggedInUser = useFetchUserDetails();

    const [searchParams] = useSearchParams();

    const clientName = searchParams.get("client_name") || "";
    const fromDateFilter = searchParams.get("from_date");
    const toDateFilter = searchParams.get("to_date");

    const getDefaultStatusForApi = () => {
        if (context === "client_chart_all_tasks") {
            return [
                TaskStatus.COMPLETED,
                TaskStatus.IN_PROGRESS,
                TaskStatus.NOT_STARTED,
            ];
        }

        return [TaskStatus.IN_PROGRESS, TaskStatus.NOT_STARTED];
    };

    const getDefaultCategoryForApi = () => {
        if (context === "dashboard_supervisee_tasks") {
            return [
                TaskCategory.REVIEW_SUPERVISEE_ADDENDUM,
                TaskCategory.REVIEW_SUPERVISEE_NOTE,
            ];
        }

        if (context.includes("client_chart")) {
            return CLIENT_CHART_TASK_CATEGORIES;
        }

        return [
            TaskCategory.WRITE_SESSION_NOTE,
            TaskCategory.MAKE_REQUESTED_CHANGE,
            TaskCategory.USER_CREATED,
            TaskCategory.WRITE_FOLLOW_UP_SAFETY_NOTE,
        ];
    };

    const hideScheduledTasks =
        context === "dashboard_my_tasks" && !clientId && !toDateFilter;

    const { isLoading, error, data, isSuccess } = useFetchTasks({
        status: getDefaultStatusForApi(),
        assignee_user_id: loggedInUser.data?.user_id || "",
        client_name: clientName,
        creator_user_id: loggedInUser.data?.user_id || "",
        viewer_user_ids: loggedInUser.data?.user_id
            ? [loggedInUser.data.user_id]
            : undefined,
        user_id_query_logic: "OR",
        from_date: fromDateFilter,
        to_date: toDateFilter,
        show_future_user_created_todos: hideScheduledTasks
            ? "false"
            : undefined,
        sort_attribute: TaskListSortAttribute.DUE_DATE,
        sort_order: AppSortOrder.ASC,
        client_id: clientId || "",
        category: getDefaultCategoryForApi(),
    });

    const isViewingPendingTasks =
        context.includes("dashboard") || context.includes("pending");

    return (
        <div className="h-full">
            {isLoading ? <Loader loaderType="section" /> : null}
            {error && error?.response?.status !== 404 && (
                <ListState
                    context="general"
                    isError
                    errorMsg={
                        error?.response?.data?.message ||
                        `${
                            isViewingPendingTasks
                                ? "Could not load pending task as this time"
                                : "Could not load tasks as this time"
                        } `
                    }
                    stateHelperText="Try reloading this page to solve this issue"
                />
            )}
            {data && isSuccess && data.data.length === 0 && (
                <ListState
                    stateHelperText={
                        hideScheduledTasks
                            ? "Tasks scheduled for later show up 2 days before they are due"
                            : "Tasks will be added when you have one or more tasks"
                    }
                    isError={false}
                    emptyMessage={
                        isViewingPendingTasks
                            ? "No pending task found"
                            : "No task found"
                    }
                    context="general"
                />
            )}
            {data && isSuccess && data.data.length > 0 && (
                <div className="flex flex-col gap-y-2">
                    {data.data.map((task) => (
                        <Task task={task} key={task.todo_id} />
                    ))}
                </div>
            )}
        </div>
    );
}
